import { Text } from "@ui-kitten/components";
import { EventAvailabilityDetails, SubscriptionDetails } from "features/types";
import i18n from "plugins/localization";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import React from "react";
import { Pressable, View } from "react-native";
import { isSportsEvent } from "utils/eventUtils";
import ScheduleItem from "./ScheduleItem";

interface ScheduleItemListProps {
  schedule: EventAvailabilityDetails[];
  selectedSubscription: SubscriptionDetails | null;
  planPackageOperable: boolean;
  onScheduleItemClick: (item: EventAvailabilityDetails) => void;
}

const divideSchedule = (
  schedule: EventAvailabilityDetails[]
): EventAvailabilityDetails[][] => {
  const reservableEvents: EventAvailabilityDetails[] = [];
  const nonReservableEvents: EventAvailabilityDetails[] = [];

  schedule.forEach((item) => {
    item.reservable
      ? reservableEvents.push(item)
      : nonReservableEvents.push(item);
  });
  return [reservableEvents, nonReservableEvents];
};

const ScheduleItemList: React.FC<ScheduleItemListProps> = ({
  schedule,
  selectedSubscription,
  planPackageOperable,
  onScheduleItemClick,
}) => {
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();

  // helpers
  let [reservableEvents, nonReservableEvents] = divideSchedule(schedule);

  // Only show event type if multiSport is enabled and eventType is not NIL, EXPERIENCES, or MUSIC
  const shouldShowEventType = (event: EventAvailabilityDetails) => {
    return Boolean(tenantConfig?.multiSport) && isSportsEvent(event.eventType);
  };

  const reservationWindow = () => {
    const THREE_DAY = 24 * 3;
    const reservationWindowHours =
      selectedSubscription?.planPackage.primaryComponent
        .reservationWindowHours || 0;
    if (reservationWindowHours > THREE_DAY) {
      return i18n.t("common.reservationWindowDay", {
        days: Math.floor(reservationWindowHours / 24),
      });
    }
    return i18n.t("common.reservationWindowHour", {
      hours: reservationWindowHours,
    });
  };

  // if current plan package is not operative, show unclickable schedule
  if (planPackageOperable === false) {
    return (
      <View style={{ opacity: 0.5 }}>
        {schedule.map((item, index) => (
          <View key={index}>
            <ScheduleItem
              event={item}
              shouldShowEventType={shouldShowEventType(item)}
            />
          </View>
        ))}
      </View>
    );
  }

  return (
    <>
      {reservableEvents.length > 0 && (
        <View style={{ marginBottom: 15 }}>
          {reservableEvents.map((item, index) =>
            item.openForReservations ? (
              <Pressable
                key={index}
                onPress={() => onScheduleItemClick(item)}
                testID={`schedule-item`}
              >
                <ScheduleItem
                  event={item}
                  shouldShowEventType={shouldShowEventType(item)}
                />
              </Pressable>
            ) : (
              // if an event is reservable but is not open for reservation, gray it out
              <View key={index} style={{ opacity: 0.3 }}>
                <ScheduleItem
                  event={item}
                  shouldShowEventType={shouldShowEventType(item)}
                />
              </View>
            )
          )}
        </View>
      )}

      {nonReservableEvents.length > 0 && (
        <>
          <View style={{ marginBottom: 15 }}>
            <Text style={{ fontWeight: "700", fontSize: 20 }}>
              {i18n.t("screens.Schedule.eventsAvailableSoon")}
            </Text>
            <Text style={{ fontSize: 13, color: "rgba(0,0,0,0.87)" }}>
              {i18n.t("screens.Schedule.outSideReservationWindow", {
                window: reservationWindow(),
              })}
            </Text>
          </View>
          <View style={{ opacity: 0.3 }}>
            {nonReservableEvents.map((item, index) => (
              <ScheduleItem
                event={item}
                key={index}
                shouldShowEventType={shouldShowEventType(item)}
              />
            ))}
          </View>
        </>
      )}
    </>
  );
};

export default ScheduleItemList;
